/**
 * Select2 Initialization
 *
 * Inizializza Select2 su tutti i <select> presenti nel DOM
 * che non sono già stati inizializzati da altri script o plugin di terze parti.
 *
 * Logica anti-conflitto:
 * - Skippa i select che hanno già la classe `select2-hidden-accessible`
 *   (aggiunta automaticamente da Select2 quando inizializza un elemento)
 * - Skippa i select con attributo `data-no-select2` (opt-out esplicito)
 * - Skippa i select dentro `.wc-enhanced-select`, `.select2-container` e
 *   aree admin (`#wpadminbar`, `#adminmenuwrap`, `.wp-admin`) per non
 *   interferire con WooCommerce, ACF o altri plugin che gestiscono
 *   autonomamente la propria istanza di Select2.
 *
 * @package Celada
 */

import 'select2';
// CSS strutturale di select2 (posizionamento, display, overflow…) → va in vendors.css
// I nostri override visivi sono in src/scss/components/_select2.scss → main.css (caricato dopo)
import 'select2/dist/css/select2.min.css';

/**
 * Stringhe di localizzazione per select2.
 * Lette dal globale `celadaSelect2I18n` iniettato da wp_localize_script() in PHP.
 * WPML scansiona le stringhe __() nel PHP e le registra per la traduzione.
 * Fallback italiano per ambienti non-WordPress.
 */
function getSelect2Language() {
	const s = (typeof celadaSelect2I18n !== 'undefined') ? celadaSelect2I18n : {};

	const t = (key, fallback) => s[key] || fallback;

	return {
		errorLoading:    () => t('errorLoading', 'I risultati non possono essere caricati.'),
		inputTooLong:    (args) => {
			const n = args.input.length - args.maximum;
			return n === 1
				? t('inputTooLong1', 'Per favore cancella 1 carattere')
				: t('inputTooLong', 'Per favore cancella %d caratteri').replace('%d', n);
		},
		inputTooShort:   (args) => {
			const n = args.minimum - args.input.length;
			return t('inputTooShort', 'Per favore inserisci almeno %d caratteri').replace('%d', n);
		},
		loadingMore:     () => t('loadingMore', 'Caricando più risultati…'),
		maximumSelected: (args) => args.maximum === 1
			? t('maxSelected1', 'Puoi selezionare solo 1 elemento')
			: t('maxSelected', 'Puoi selezionare solo %d elementi').replace('%d', args.maximum),
		noResults:       () => t('noResults', 'Nessun risultato trovato'),
		searching:       () => t('searching', 'Sto cercando…'),
		removeAllItems:  () => t('removeAllItems', 'Rimuovi tutti gli elementi'),
	};
}

let $ = jQuery.noConflict();

/**
 * Selettori da escludere dall'inizializzazione automatica del tema.
 * I select dentro questi wrapper vengono gestiti da plugin di terze parti.
 */
const EXCLUDED_CONTEXTS = [
	'.select2-container',        // già wrappato da select2
	'#wpadminbar',               // toolbar WordPress
	'.wp-admin',                 // pagine di amministrazione
	'.woocommerce-select2',      // WooCommerce widget
	'.acf-field',                // ACF fields (gestiti da ACF stesso)
].join(', ');

/**
 * Verifica se un elemento è già stato inizializzato da Select2
 * (da qualsiasi sorgente: tema, plugin, core WP).
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isAlreadyInitialized(el) {
	return (
		$(el).hasClass('select2-hidden-accessible') ||
		!!$(el).data('select2')
	);
}

/**
 * Ricava il placeholder da usare per il select2.
 *
 * Priorità:
 * 1. `data-placeholder` esplicito sull'elemento → usato as-is
 * 2. Testo della prima <option> vuota (es. "Seleziona Regione", "--Nessuno--")
 * 3. Stringa vuota come fallback
 *
 * Il <label> non viene più usato come placeholder né nascosto: il placeholder
 * sparisce appena si sceglie un valore, quindi l'etichetta deve restare sempre
 * visibile (WCAG 3.3.2 / 1.3.1).
 *
 * @param {jQuery} $select
 * @returns {string}
 */
function resolvePlaceholder($select) {
	// 1. Placeholder esplicito — ha la priorità assoluta
	const explicit = $select.data('placeholder');
	if (explicit !== undefined && explicit !== '') {
		return String(explicit);
	}

	// 2. Prima option con value vuoto
	const $empty = $select.find('option').filter(function () {
		return $(this).val() === '';
	}).first();

	if ($empty.length) {
		return $empty.text().trim();
	}

	return '';
}

/**
 * Inizializza Select2 su tutti i <select> eligibili nella pagina.
 * Si può richiamare anche dopo contenuto aggiunto dinamicamente (AJAX).
 *
 * @param {HTMLElement|jQuery} [context=document] - Contesto entro cui cercare i select.
 */
export function initSelect2(context = document) {
	const $selects = $('select', context).filter(function () {
		// 1. Salta se già inizializzato
		if (isAlreadyInitialized(this)) {
			return false;
		}

		// 2. Salta se opt-out esplicito tramite attributo data
		if ($(this).is('[data-no-select2]')) {
			return false;
		}

		// 3. Salta se è dentro un contesto gestito da terze parti
		if ($(this).closest(EXCLUDED_CONTEXTS).length) {
			return false;
		}

		return true;
	});

	if (!$selects.length) {
		return;
	}

	$selects.each(function () {
		const $select = $(this);

		// Opzioni personalizzabili tramite data attributes:
		// data-placeholder, data-allow-clear, data-minimum-results-for-search
		const options = {
			placeholder:              resolvePlaceholder($select),
			allowClear:               $select.data('allow-clear')              !== undefined
				? !!$select.data('allow-clear')
				: false,
			minimumResultsForSearch:  $select.data('minimum-results-for-search') !== undefined
				? $select.data('minimum-results-for-search')
				: 0, // 0 = mostra sempre la searchbox, evita la selezione accidentale
				     // della prima voce al mouseup. Per nasconderla usare data-minimum-results-for-search="-1"
			width:                    $select.data('width') || 'resolve',
			language:                 $select.data('language') || getSelect2Language(),
		};

		// Usa il dir attribute del documento per RTL/LTR
		if (document.documentElement.dir === 'rtl') {
			options.dir = 'rtl';
		}

		$select.select2(options);
	});
}

/**
 * Bootstrap: esegui al DOM ready.
 */
$(function () {
	initSelect2();
});
